import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Camera } from 'lucide-react';
import UploadIcon from './upload-icon.ui';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import classNames from 'classnames';
import { trackError, trackEvent } from '../analytics/analytics.service';

import styles from './file-upload.module.css';

export enum FileUploadType {
  image = 'image',
  document = 'document',
}

interface FileUploadProps {
  onFileChange: (file: File | null) => void;
  currentFile?: string | null;
  isUploading?: boolean;
  uploadType: FileUploadType;
  disabled?: boolean;
  error?: string | boolean | undefined;
  hasFileUploaded?: boolean;
  fileName?: string;
  onError?: (error: string | null) => void;
}

interface FileTypeConfig {
  accept: Record<string, string[]>;
  maxSize: number;
  icon: React.ReactNode;
  primaryText?: string;
  secondaryText?: string;
  dragText?: string;
  errorMessages: {
    'file-too-large': string;
    'file-invalid-type': string;
  };
}

const FILE_TYPE_CONFIGS: Record<FileUploadType, FileTypeConfig> = {
  image: {
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    icon: <Camera size={16} />,
    primaryText: 'Add photo',
    secondaryText: 'Drag & drop or click to upload',
    dragText: 'Drop photo here',
    errorMessages: {
      'file-too-large': 'Image is too large. Maximum size is 5MB.',
      'file-invalid-type': 'Invalid file type. Please upload an image file.',
    },
  },
  document: {
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    icon: <UploadIcon />,
    errorMessages: {
      'file-too-large': 'Document is too large. Maximum size is 50MB.',
      'file-invalid-type': 'Invalid file type. Please upload a PDF, DOC, or DOCX file.',
    },
  },
};

const FileUpload = (props: FileUploadProps) => {
  const {
    onFileChange,
    currentFile,
    isUploading = false,
    uploadType,
    disabled = false,
    error = false,
    hasFileUploaded = false,
    fileName,
    onError,
  } = props;

  const [preview, setPreview] = useState<string | null>(null);
  const [hasFile, setHasFile] = useState<{ name: string; size: number } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const config = FILE_TYPE_CONFIGS[uploadType];

  useEffect(() => {
    if (currentFile) {
      if (uploadType === 'image') {
        setPreview(currentFile);
        setHasFile(null);
      } else {
        setPreview(null);
        setHasFile({
          name: currentFile.split('/').pop() || 'Current file',
          size: 0,
        });
      }
    } else {
      setPreview(null);
      setHasFile(null);
    }
  }, [currentFile, uploadType]);

  // Pass test error to parent for document uploads
  useEffect(() => {
    if (fileError && uploadType === 'document' && onError) {
      onError(fileError);
    }
  }, [fileError, uploadType, onError]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && !isUploading && !disabled) {
      // Track file selection attempt
      trackEvent('file_upload_attempt', {
        upload_type: uploadType,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        has_current_file: !!currentFile,
      });

      if (uploadType === 'document') {
        const allowedExtensions = ['.pdf', '.doc', '.docx'];
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
          const errorMessage = config.errorMessages['file-invalid-type'];

          // Track manual extension validation failure
          trackError(
            'file_upload_manual_validation_failed',
            errorMessage,
            'FileUpload.onDrop',
            {
              upload_type: uploadType,
              file_extension: fileExtension,
              file_name: file.name,
              file_size: file.size,
              file_type: file.type,
            },
          );

          setFileError(errorMessage);
          if (onError) onError(errorMessage);
          return;
        }
      }

      // Clear any previous errors when a valid file is dropped
      setFileError(null);
      if (onError) onError(null);

      try {
        if (uploadType === 'image') {
          const reader = new FileReader();
          reader.onload = (e) => {
            setPreview(e.target?.result as string);
            // Track successful preview generation
            trackEvent('file_preview_generated', {
              upload_type: uploadType,
              file_name: file.name,
            });
          };
          reader.onerror = (e) => {
            // Track FileReader error
            trackError(
              'file_reader_error',
              'Failed to read file for preview',
              'FileUpload.FileReader',
              {
                upload_type: uploadType,
                file_name: file.name,
                file_size: file.size,
              },
            );
          };
          reader.readAsDataURL(file);
          setHasFile(null);
        } else {
          // For documents, just store file info without preview
          setPreview(null);
          setHasFile({
            name: file.name,
            size: file.size,
          });
        }

        onFileChange(file);

        // Track successful file selection
        trackEvent('file_upload_selected', {
          upload_type: uploadType,
          file_name: file.name,
          file_size: file.size,
        });
      } catch (error) {
        // Track file processing error
        trackError(
          'file_processing_error',
          error instanceof Error ? error.message : 'Unknown error processing file',
          'FileUpload.onDrop',
          {
            upload_type: uploadType,
            file_name: file.name,
            file_size: file.size,
            error_details: error instanceof Error ? error.stack : String(error),
          },
        );
      }
    }
  }, [onFileChange, isUploading, disabled, uploadType, config.errorMessages, onError, currentFile]);

  const onDropRejected = useCallback((rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      const firstFile = rejectedFiles[0];
      const firstError = firstFile.errors?.[0];

      let errorMessage = '';
      if (firstError?.code === 'file-too-large') {
        errorMessage = config.errorMessages['file-too-large'];
      } else if (firstError?.code === 'file-invalid-type') {
        errorMessage = config.errorMessages['file-invalid-type'];
      } else {
        errorMessage = 'File upload failed. Please try again.';
      }

      trackError(
        'file_upload_rejected',
        errorMessage,
        'FileUpload',
        {
          upload_type: uploadType,
          error_code: firstError?.code || 'unknown',
          file_name: firstFile.file?.name,
          file_size: firstFile.file?.size,
          has_current_file: !!currentFile,
          current_file_url: currentFile || 'none',
        },
      );

      setFileError(errorMessage);
      if (onError) onError(errorMessage);
    }
  }, [config.errorMessages, onError, uploadType, currentFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: config.accept,
    maxSize: config.maxSize,
    multiple: false,
    disabled: isUploading || disabled,
  });

  // Combine all error sources
  const displayError = fileError || error;

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={styles.fileUpload}>
      <div
        {...getRootProps()}
        className={classNames(styles.fileUploadContainer, {
          [styles.fileUploadContainerDragging]: isDragActive,
          [styles.fileUploadContainerError]: displayError,
          [styles.fileUploadContainerUploading]: isUploading,
          [styles.fileUploadContainerDisabled]: disabled,
        })}
        data-upload-type={uploadType}
      >
        <input {...getInputProps()} />

        {preview ? (
          // Only show preview for images
          <div className={styles.fileUploadPreview}>
            <img
              src={preview}
              alt="Preview"
              className={styles.fileUploadImage}
            />
            {isUploading ? (
              <div className={styles.fileUploadUploadingOverlay}>
                <div className={styles.fileUploadSpinner}></div>
                <span>Uploading...</span>
              </div>
            ) : (
              <div className={styles.fileUploadOverlay}>
                <Camera size={20} color="white" />
                <span>Change Photo</span>
              </div>
            )}
          </div>
        ) : hasFile && uploadType === 'image' ? (
          // Show file info only for images, not documents
          <div className={styles.fileUploadFileInfo}>
            <div className={styles.fileUploadFileDetails}>
              <div className={styles.fileUploadFileText}>
                <span className={styles.fileUploadFileName}>
                  {hasFile.name}
                </span>
                {hasFile.size > 0 && (
                  <span className={styles.fileUploadFileSize}>
                    {formatFileSize(hasFile.size)}
                  </span>
                )}
              </div>
            </div>
            {isUploading && (
              <div className={styles.fileUploadUploadingOverlay}>
                <div className={styles.fileUploadSpinner}></div>
                <span>Uploading...</span>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.fileUploadPlaceholder}>
            {isUploading ? (
              <div className={styles.fileUploadUploadingState}>
                <div className={styles.fileUploadSpinner}></div>
                <span>Uploading...</span>
              </div>
            ) : (
              <>
                <div className={styles.fileUploadIcon}>
                  {config.icon}
                  {((hasFile || currentFile || hasFileUploaded || fileName) && uploadType === 'document') && (
                    <span className={styles.downloadDoneIcon}>
                      <DownloadDoneIcon style={{ fontSize: '.5rem' }} />
                    </span>
                  )}
                </div>
                {uploadType === 'image' && (
                  <div className={styles.fileUploadText}>
                    <p className={styles.fileUploadPrimaryText}>
                      {isDragActive ? config.dragText : config.primaryText}
                    </p>
                    <div className={styles.fileUploadSecondaryText}>
                      <span className={styles.fileUploadSecondaryDesktop}>
                        {config.secondaryText}
                      </span>
                      <span className={styles.fileUploadSecondaryMobile}>
                        {config.secondaryText && config.secondaryText.replace('Drag & drop or ', '')}
                      </span>
                    </div>
                  </div>
                )}
                {uploadType === 'document' && (hasFile || currentFile || fileName) && (
                  <div className={styles.fileUploadText}>
                    <div className={styles.fileUploadPrimaryText}>
                      {hasFile?.name || fileName || 'Document uploaded'}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {(displayError && uploadType === 'image') && (
        <div className={styles.error}>
          {displayError}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
